import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from "@mui/material";
import AvailableSlotsHeader from "ui/appointment/components/common/availableSlots/AvailableSlotsHeader/AvailableSlotsHeader";
import { sxStyles } from "./TcuSlotsSelectionPage.styles";
import AppointmentDetailsNoticePanel from "ui/appointment/components/common/cards/AppointmentDetailsNoticePanel/AppointmentDetailsNoticePanel";
import IMAGES from "lib/assets/images";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import {
  selectCreateAppointment,
  selectCreateTcuAppointment,
} from "ui/appointment/ducks/selectors";
import { useEffect, useState } from "react";
import VerticalSpreadLayout from "lib/components/layout/VerticalSpreadLayout/VerticalSpreadLayout";
import { formatDate } from "lib/util/DateTimeUtil/formatDate/formatDate";
import { useNavigate } from "react-router";
import { useGetMessageAction } from "lib/routing/messageChannel/hooks/useGetMessageAction";
import { PATHS } from "lib/routing";
import ButtonsFooter from "lib/components/buttons/ButtonsFooter/ButtonsFooter";
import { updateTcuRescheduleSlot } from "ui/appointment/ducks/appointmentsSlice";

/* 
This component is only to be used for TCU flow. Any other appointment creation or reschedule should go via availableSlotsSelection
 */
const TcuSlotsSelectionPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = sxStyles(theme);

  const { messageActionObj, consumeMessageActionObj } = useGetMessageAction();

  // Redux States
  const { bookedSlot, otherSlots } = useAppSelector(selectCreateTcuAppointment);
  const { selectedInstitution } = useAppSelector(selectCreateAppointment);
  const bookedSlotDetails = bookedSlot?.slots.map((slot) => {
    return [formatDate(slot.date), slot.serviceType];
  });

  // Local States
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isNextEnabled, setIsNextEnabled] = useState(false);

  // Handlers
  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedIndex(parseInt(event.target.value));
  };
  const handleOnClickNext = () => {
    dispatch(updateTcuRescheduleSlot(otherSlots?.[selectedIndex ?? 0] ?? null));
    navigate(PATHS.APPOINTMENT_RESCHEDULE_TCU_CONFIRM.path);
  };
  const handleOnClickBack = () => {
    navigate(PATHS.APPOINTMENT_MOBILE.path, { replace: true });
  };

  // Effects
  useEffect(() => {
    if (messageActionObj?.action === "backTriggered") {
      navigate(PATHS.APPOINTMENT_MOBILE.path, { replace: true });
      consumeMessageActionObj();
    }
  }, [consumeMessageActionObj, dispatch, messageActionObj?.action, navigate]);

  useEffect(() => {
    if (otherSlots && selectedIndex !== null) {
      setIsNextEnabled(true);
    }
  }, [otherSlots, selectedIndex]);

  return (
    <>
      <VerticalSpreadLayout>
        <Box>
          {selectedInstitution ? (
            <AvailableSlotsHeader location={selectedInstitution} />
          ) : null}

          <Typography sx={classes.title}>
            Booked Appointment(s) Details
          </Typography>

          {/* AutoGen Booked Appt Details */}
          {bookedSlotDetails ? (
            <Box sx={classes.bookedSlotDetailsContainer}>
              <AppointmentDetailsNoticePanel
                detailsList={bookedSlotDetails}
                tagLabel="BOOKED"
                tagColorChoice="green"
              />
            </Box>
          ) : null}

          <Typography sx={classes.slotsSectionTitle}>
            Choose a New Time Slot
          </Typography>

          {/* Slots radio list */}
          <Box sx={classes.radioListContainer}>
            <FormControl component="fieldset" sx={classes.layout}>
              <RadioGroup
                aria-label="available-slots"
                name="available-slots"
                value={selectedIndex}
                onChange={handleOnChange}
              >
                {otherSlots?.map((bundledSlot, index) => {
                  return (
                    <FormControlLabel
                      sx={classes.listItem}
                      key={index}
                      value={index}
                      label={bundledSlot.slots.map((slot, index) => {
                        return (
                          <Box key={index}>
                            <Typography sx={classes.listItemText}>
                              {formatDate(slot.date)}
                            </Typography>
                            <Typography sx={classes.listItemText}>
                              {slot.serviceType}
                            </Typography>
                          </Box>
                        );
                      })}
                      control={
                        <Radio
                          icon={
                            <Box
                              component={"img"}
                              src={IMAGES.general.RadioButtonUncheckedIcon}
                              sx={classes.radio}
                            />
                          }
                          checkedIcon={
                            <Box
                              component={"img"}
                              src={IMAGES.general.RadioButtonCheckedIcon}
                              sx={classes.radio}
                            />
                          }
                          sx={classes.radio}
                          color="primary"
                        />
                      }
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>

        <Box sx={classes.bottomContainer}>
          <ButtonsFooter
            nextButtonText={"Next"}
            isDisabled={!isNextEnabled}
            onClickNext={handleOnClickNext}
            cancelButtonText={"Back to Appointment"}
            onClickCancel={handleOnClickBack}
          />
        </Box>
      </VerticalSpreadLayout>
    </>
  );
};

export default TcuSlotsSelectionPage;
